/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { addDays } from "date-fns"

/**
 * The real M4 slot engine clamps availability to `[now + minLeadHours, now + horizonWeeks]`
 * (served by `GET /booking/config`, 03 §4). The Time step pages that horizon **one week at a
 * time** so the day-strip stays navigable and no single request pulls the whole grid —
 * `getAvailableSlots(from, to, tz)` is called once per week page (the backend re-clamps, so an
 * out-of-range page just returns fewer slots — page 0's first days are empty by the lead time).
 *
 * The horizon (page count) comes from `config.horizonWeeks` — passed into `clampPage`, never
 * hardcoded here, so the pager never drifts from the backend.
 */
export const PAGE_DAYS = 7 // one page = one week

export type SlotPage = { from: Date; to: Date }

// Offset (ms) of `timeZone` at `instant` — via Intl, so no tz library ships in the bundle.
const zoneOffsetMs = (instant: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
  const parts: Record<string, string> = {}
  for (const part of formatter.formatToParts(instant)) {
    parts[part.type] = part.value
  }
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // some ICU versions render midnight as "24"
    Number(parts.minute),
    Number(parts.second)
  )
  return asUtc - instant.getTime()
}

/**
 * Anchor the pager to the START OF `now`'s day **in the DISPLAY time zone** — the zone the
 * day-strip renders. The strip shows whole display-zone calendar days, so the page window must
 * tile those exact days: `[dayStart + 7·i, dayStart + 7·(i+1))`. Anchoring to any OTHER zone's
 * midnight (the original bug: the browser's local day — e.g. a Belgrade browser displaying
 * Eastern Time) shifts every window boundary by the zone gap, and the display-day sitting in
 * that gap falls between the cracks permanently: its slots arrive in page N's window (which has
 * no cell for that day, so they're dropped) while page N+1's window starts after they end — the
 * day then shows as empty on every page.
 */
export const pagingBase = (now: Date, timeZone: string): Date => {
  // `now`'s wall-clock date in the zone (en-CA → "YYYY-MM-DD").
  const [year, month, day] = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(now)
    .split("-")
    .map(Number)
  // That date's zone-midnight as a UTC instant: start from UTC midnight, correct by the zone
  // offset; the second pass covers a DST flip sitting between the guess and the true midnight.
  const utcMidnight = Date.UTC(year, month - 1, day)
  const guess = new Date(utcMidnight - zoneOffsetMs(new Date(utcMidnight), timeZone))
  return new Date(utcMidnight - zoneOffsetMs(guess, timeZone))
}

/** The `[from, to)` window for page `index` (0-based), anchored at a stable `base` ("now"). */
export const pageWindow = (base: Date, index: number): SlotPage => ({
  from: addDays(base, index * PAGE_DAYS),
  to: addDays(base, (index + 1) * PAGE_DAYS),
})

/**
 * Keep a page index within `[0, pageCount - 1]` so prev/next can't leave the horizon.
 * `pageCount` = `config.horizonWeeks` (one page per week).
 */
export const clampPage = (index: number, pageCount: number): number => Math.min(pageCount - 1, Math.max(0, index))
